import { getAuth, isOpenApi } from '../services/utils';
import { jwtDecode } from 'jwt-decode';

// 適康復健科的五間診所 Client ID (UUID 格式的 basicID)
const SHIKANG_CLINIC_CLIENT_IDS = [
  'c7104950-e56e-11ed-a2ac-b9cd6c37b97e', // 信義適康復健科診所 (surecare)
  '63cc5290-5ab5-11ef-aeb9-193ca4214e58', // 內湖適康復健科診所 (neihuscare)
  '5b14b350-5ad2-11ef-88e8-e3877dcfb7f4', // 古亭適康復健科診所 (csscare)
  '53881240-5abd-11ef-b676-6ffeb9ba05cc', // 中山適康復健科診所 (zsscare)
  '238aac90-0b73-11ef-9dc8-7b3ada28a529', // 竹北適康復健科診所 (zhubeishikang)
];

// Staging 測試用 Client ID (使用 Demo 國泰中醫測試)
const STAGING_TEST_CLIENT_IDS = [
  '7e8d5be0-3611-11ec-a7a0-fb1f4d46424b', // Demo 國泰中醫
];

// GTM ID 配置（根據環境決定）
const isStaging = process.env.GATSBY_STAGE !== 'production';

// Production: GTM-K7PR3M33 (適康體系提供的 GTM)
// Staging: GTM-MLC6W43B (AlleyPin 測試用 GTM)
const CLINIC_GTM_ID = isStaging ? 'GTM-MLC6W43B' : 'GTM-K7PR3M33';
const CLINIC_DATA_LAYER = 'clinicDataLayer';

/**
 * 從 localStorage 的 token 中取得 clientId
 */
export const getClientIdFromToken = () => {
  try {
    const auth = getAuth();
    if (!auth || !auth.Authorization) {
      return null;
    }

    const token = auth.Authorization.replace('Bearer ', '');
    const decoded = jwtDecode(token);

    // 根據 API 類型取得 clientId
    if (isOpenApi()) {
      return decoded.clientID;
    } else {
      return decoded?.data?._clinic_id;
    }
  } catch (error) {
    console.log('[Clinic GTM] Failed to decode token:', error);
    return null;
  }
};

/**
 * 為適康復健科診所頁面載入客製化 GTM
 * 使用獨立的 clinicDataLayer 確保與全站 GTM (GTM-5BDXVRV7) 完全隔離
 *
 * 判斷方式：透過 token 中的 clientId 判斷診所
 *
 * 環境配置：
 * - Production: GTM-K7PR3M33 (適康體系提供)
 * - Staging: GTM-MLC6W43B (AlleyPin 測試用)
 *
 * 適用診所：
 * - 信義適康復健科診所 (surecare)
 * - 內湖適康復健科診所 (neihuscare)
 * - 古亭適康復健科診所 (csscare)
 * - 中山適康復健科診所 (zsscare)
 * - 竹北適康復健科診所 (zhubeishikang)
 *
 * @note 如果 LIFF init 後立即跳轉到 PinMed，此 GTM 可能來不及載入
 *       但 PinMed 端也有對應的 GTM 實作 (見 https://github.com/AlleyPin/PinMed/pull/668)
 *       因此追蹤覆蓋完整，不會有空窗期
 */
export const initClinicGTM = () => {
  // 從 token 取得 clientId
  const clientIdFromToken = getClientIdFromToken();

  // 檢查是否為適康復健科診所
  const isShikangClinic =
    clientIdFromToken && SHIKANG_CLINIC_CLIENT_IDS.includes(clientIdFromToken);

  // Staging 環境允許測試診所
  const isStagingTestClinic =
    isStaging &&
    clientIdFromToken &&
    STAGING_TEST_CLIENT_IDS.includes(clientIdFromToken);

  if (!isShikangClinic && !isStagingTestClinic) {
    return;
  }

  // 檢查是否已經載入過
  if (window[`__CLINIC_GTM_LOADED__`]) {
    console.log(`[Clinic GTM] Already loaded`);
    return;
  }

  try {
    // 初始化獨立的 dataLayer
    window[CLINIC_DATA_LAYER] = window[CLINIC_DATA_LAYER] || [];
    window[CLINIC_DATA_LAYER].push({
      'gtm.start': new Date().getTime(),
      event: 'gtm.js',
    });

    // 動態插入 GTM script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${CLINIC_GTM_ID}&l=${CLINIC_DATA_LAYER}`;

    const firstScript = document.getElementsByTagName('script')[0];
    firstScript.parentNode.insertBefore(script, firstScript);

    // 動態插入 noscript iframe (for fallback)
    const noscript = document.createElement('noscript');
    const iframe = document.createElement('iframe');
    iframe.src = `https://www.googletagmanager.com/ns.html?id=${CLINIC_GTM_ID}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';
    noscript.appendChild(iframe);
    document.body.insertBefore(noscript, document.body.firstChild);

    // 標記已載入
    window[`__CLINIC_GTM_LOADED__`] = true;

    console.log(
      `[Clinic GTM] Loaded ${CLINIC_GTM_ID} (${
        isStaging ? 'Staging Test' : 'Shikang Production'
      })`
    );
    console.log(
      `[Clinic GTM] - Environment: ${isStaging ? 'Staging' : 'Production'}`
    );
    console.log(`[Clinic GTM] - clientId: ${clientIdFromToken}`);
    console.log(
      `[Clinic GTM] - Using isolated dataLayer: ${CLINIC_DATA_LAYER}`
    );
  } catch (error) {
    console.error('[Clinic GTM] Failed to load:', error);
  }
};
